import { useTranslation } from 'react-i18next';
import type { AnalyticsFilters } from '@/types/analytics.types';
import type { FundamentalsResponse } from '@/api/types';
import { useOUs } from '@/hooks/useOUs';
import { getCurrentMonthRange } from '../utils/dateRange';

const { dateFrom: DEFAULT_DATE_FROM, dateTo: DEFAULT_DATE_TO } = getCurrentMonthRange();

interface FilterBarProps {
  filters: AnalyticsFilters;
  setFilter: <K extends keyof AnalyticsFilters>(key: K, value: AnalyticsFilters[K]) => void;
  resetFilters: () => void;
  apiFundamentals?: FundamentalsResponse;
}

export function FilterBar({ filters, setFilter, resetFilters, apiFundamentals }: FilterBarProps) {
  const { t } = useTranslation();
  const { data: ous = [] } = useOUs();

  const hasFundamentals = !!apiFundamentals && apiFundamentals.length > 0;
  const fundOptions = hasFundamentals
    ? apiFundamentals.map((f) => ({ id: f.id, name: f.name }))
    : [];

  const hasFilters =
    filters.ou !== '' ||
    filters.bu !== '' ||
    filters.dept !== '' ||
    filters.gerencia !== '' ||
    filters.fundFilter !== '' ||
    filters.dateFrom !== DEFAULT_DATE_FROM ||
    filters.dateTo !== DEFAULT_DATE_TO;

  return (
    <div className="au-filter-bar">
      {/* Date range */}
      <input
        type="date"
        className="au-date-input"
        value={filters.dateFrom}
        onChange={(e) => setFilter('dateFrom', e.target.value)}
        aria-label={t('analytics.filterBar.dateFrom', 'Desde')}
      />
      <span style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>–</span>
      <input
        type="date"
        className="au-date-input"
        value={filters.dateTo}
        onChange={(e) => setFilter('dateTo', e.target.value)}
        aria-label={t('analytics.filterBar.dateTo', 'Hasta')}
      />

      <div className="au-filter-bar-sep" />

      {/* OU selector from catalog */}
      <select
        className="au-cascade-select"
        value={filters.ou}
        onChange={(e) => {
          setFilter('ou', e.target.value);
          setFilter('bu', '');
          setFilter('dept', '');
        }}
        aria-label={t('analytics.filterBar.allOrg')}
      >
        <option value="">{t('analytics.filterBar.allOrg')}</option>
        {ous.map((ou) => (
          <option key={ou.key} value={ou.key}>
            {ou.label}
          </option>
        ))}
      </select>

      <div className="au-filter-bar-sep" />

      {/* Fundamentals selector */}
      <select
        className="au-cascade-select"
        value={filters.fundFilter}
        onChange={(e) => setFilter('fundFilter', e.target.value)}
        aria-label={t('analytics.filterBar.allFund')}
        disabled={!hasFundamentals}
      >
        {hasFundamentals ? (
          <>
            <option value="">{t('analytics.filterBar.allFund')}</option>
            {fundOptions.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </>
        ) : (
          <option value="">{t('common.loading')}</option>
        )}
      </select>

      {hasFilters && (
        <button
          className="au-clear-filters"
          onClick={resetFilters}
          title={t('analytics.filterBar.clearFilters', 'Limpiar filtros')}
          aria-label={t('analytics.filterBar.clearFilters', 'Limpiar filtros')}
        >
          <i className="fas fa-times" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
